import {CountDownTimerSettings} from 'Event/Dashboard/components/CountDownTimer'
import {buildLabelPlaceholders} from 'Event/Dashboard/components/CountDownTimer/CountDownTimerConfig'
import {useAddTranslation, useTranslationLanguage} from 'Event/LanguageProvider'
import {Localization} from 'Event/LanguageProvider/translations'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {generateHashId} from 'lib/crypto/hash'
import {parseVariables} from 'lib/template'

/**
 * Creates a copy of the given timer with a new id, placed at the end of the
 * list. Labels are reset to placeholders pointing at the new id - their text
 * is duplicated separately via {@link duplicateCountDownTimerLabels}.
 */
export function createDuplicateCountDownTimer(
  source: CountDownTimerSettings,
  position: number,
): Promise<{id: string; timer: CountDownTimerSettings}> {
  return generateHashId([
    'countdown_timer',
    Date.now().toString(),
    Math.random().toString(),
  ]).then((hashId) => {
    const id = `countdown_${hashId}`

    return {
      id,
      timer: {
        ...source,
        position,
        labels: buildLabelPlaceholders(id),
      },
    }
  })
}

/**
 * Builds the translation entries for a duplicated timer, copying the source
 * timer's resolved label text onto the new timer's placeholder keys.
 */
export function duplicateCountDownTimerLabels(props: {
  id: string
  source: CountDownTimerSettings
  translations: Record<string, string>
}): Record<string, string> {
  const {id, source, translations} = props

  // Labels are stored as `{{variable}}` placeholders, so resolve the source's
  // current text from translations, falling back to the raw text.
  const value = (text?: string) => {
    const [variable] = parseVariables(text ?? '')
    return variable ? translations[variable] ?? '' : text ?? ''
  }

  return {
    [`countdown_timer_items_${id}_labels_days`]: value(source.labels.days),
    [`countdown_timer_items_${id}_labels_hours`]: value(source.labels.hours),
    [`countdown_timer_items_${id}_labels_minutes`]: value(
      source.labels.minutes,
    ),
    [`countdown_timer_items_${id}_labels_seconds`]: value(
      source.labels.seconds,
    ),
    [`countdown_timer_items_${id}_labels_description`]: value(
      source.labels.description,
    ),
    [`countdown_timer_items_${id}_labels_separator`]: value(
      source.labels.separator,
    ),
  }
}

export function useDuplicateCountDownTimer(props: {
  countDownTimers: Record<string, CountDownTimerSettings>
  localization: Localization | null
}) {
  const {countDownTimers, localization} = props
  const save = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const targetLanguage = useTranslationLanguage()

  return (sourceId: string) => {
    const source = countDownTimers[sourceId]
    const position = Object.keys(countDownTimers).length + 1
    const translations = localization?.translations?.[targetLanguage] ?? {}

    return createDuplicateCountDownTimer(source, position).then(
      ({id, timer}) => {
        save({
          countDownTimers: {
            [id]: timer,
          },
          localization: addTranslation(
            duplicateCountDownTimerLabels({id, source, translations}),
          ),
        })
      },
    )
  }
}
